import { useTheme } from '@mui/material';
import { WorkoutLayer } from '@strength/db-schema';
import { Metric } from '@strength/domain';
import {
  getDefaultSetMetrics,
  mergeWorkoutLayers,
  SetMetricMutationData,
  Workout,
  WorkoutAlternateGroup,
  WorkoutExercise,
  WorkoutSection,
  WorkoutSuperset,
} from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import { nextSortOrder, sortByOrder } from '@strength/utils';
import { MdOutlineNote } from '@teamworksdev/icons';
import { DragEndEvent } from '@dnd-kit/core';
import { atom, useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getSupersetIndexMap,
  SupersetIcon,
} from '../../../../components/icons/SupersetIcon';
import { CELL_CLASSNAME_SELECTED, Sheet } from '../../../../components/sheet';
import { CellInputWithAtom } from '../../../../components/sheet/slots/common';
import { CustomSlot, MetricsSlot } from '../../../../components/sheet/slots';
import {
  ADD_SECTION_SLOT,
  ADD_SET_SLOT,
  EXERCISE_NAME_SLOT,
} from '../../../../components/sheet/slots/slot-labels';
import { cn } from '../../../../components/utils';
import { useBenchmarks } from '../../../../hooks/useBenchmarks';
import { toast } from '../../../../hooks/useToast';
import { useMetrics } from '../../../../hooks/useMetrics';
import { jotaiStore } from '../../../../utils/jotai';
import {
  calculateSheetDimensions,
  getGridTemplateColumns,
  getWorkoutTemplateGridStyle,
} from '../../../block/grid-utils';
import { InstructionDrawer } from '../../../block/instruction-drawer';
import { getContextMenuContent } from '../../../block/context-menu/context-menu';
import { pasteCommands } from '../../../block/copy-paste/copy-paste';
import { groupSelectionData } from '../../../block/copy-paste/group-data';
import {
  buildWorkoutClipboard,
  workoutClipboardAtom,
} from '../../../block/copy-paste/workout-clipboard';
import {
  blockSectionAndExerciseLabelStyle,
  buildSwapExerciseArgs,
  collapsedSectionsAtom,
  inputAtom as sectionInputAtom,
  SlotsResults,
} from '../../../block/slots/common';
import { CollapseSlotIcon } from '../../../block/slots/CollapseSlotIcon';
import {
  AddSetButton,
  ExerciseSearch,
  ExerciseText,
  getAddExerciseSlot,
  useCreateExercise,
} from '../../../block/slots/exercises';
import { AdvancedExerciseSearchModal } from '../../../block/advanced-search/AdvancedExerciseSearchModal';
import { SetMetricCellContent } from '../../../block/slots/sets';
import {
  WorkoutLayerCommand,
  WorkoutLayerMutationFns,
} from '../../../block/mutation-fns/types';
import { makeWorkoutLayerMutationFns } from '../../../block/mutation-fns/workout-draft';
import {
  BlockBuilderExercise,
  BlockSelectionData,
  BlockSlot,
  InstructionEditData,
} from '../../../block/types';
import {
  WorkoutExerciseDraggable,
  WorkoutSectionDraggable,
  exerciseDndStyleInfo,
  handleDragEnd,
  sectionDndStyleInfo,
  useDndRow,
} from '../../../block/dnd';
import {
  DndRowState,
  DraggableLabelWrapper,
} from '../../../block/slots/DraggableLabelWrapper';
import {
  exercisesForSection,
  isLastExerciseInGroup,
} from '../../../block/utils';
import { useViewer } from '../../../../context/viewer';
import { CreateEditExerciseSheet } from '../../exercises/components/CreateEditExerciseSheet';
import { wrapTemplateLayerAsHasWorkoutLayer } from '../workout-template-utils';

export const WorkoutTemplateSheet = ({
  layers,
  readOnly = true,
  onLayersChange,
  sheetClassName,
  showSetsColumn,
}: {
  layers: WorkoutLayer[];
  readOnly?: boolean;
  onLayersChange?: (layers: WorkoutLayer[]) => void;
  /**
   * Whether to render the Sets column. Defaults to `!readOnly` (read-only
   * sheets hide it, matching block sheets). Read-only hosts that still need
   * set prescriptions visible — e.g. the workout-import preview — pass `true`.
   */
  showSetsColumn?: boolean;
  /**
   * Extra classes for the underlying Sheet's scroll container. Merged after the
   * defaults via `cn` (tailwind-merge), so callers can override them — e.g. an
   * embedded, page-scrolled host passing `overscroll-auto` to undo the Sheet's
   * default `overscroll-none`, which otherwise traps the wheel and stops the
   * page scrolling once the cursor is over the grid.
   */
  sheetClassName?: string;
}) => {
  const {
    palette: { greyscaleLight },
  } = useTheme();
  const { t } = useTranslation();
  const {
    getMetric,
    getMetrics,
    setsMetric,
    isLoading: metricsLoading,
  } = useMetrics();
  const { defaultBenchmarkGridId } = useBenchmarks();

  const [localLayers, setLocalLayers] = useState<WorkoutLayer[]>(layers);
  const [instructionEditData, setInstructionEditData] =
    useState<InstructionEditData | null>(null);
  const [isModalOrDrawerOpen, setIsModalOrDrawerOpen] = useState(false);
  const collapsedSections = useAtomValue(collapsedSectionsAtom);

  const metrics = useMemo(() => getMetrics(true), [getMetrics]);

  // Read-only: derive display data from the prop so external layer changes are reflected.
  // Editable: use local state as the working copy; onLayersChange keeps the parent in sync.
  const activeLayers = readOnly ? layers : localLayers;

  const workoutLayersForMutation = useMemo(
    () => activeLayers.map(wrapTemplateLayerAsHasWorkoutLayer),
    [activeLayers],
  );

  const layersRef = useRef(localLayers);
  layersRef.current = localLayers;

  const mutateFn = useCallback(
    (command: WorkoutLayerCommand) => {
      const edit = command.edits[0];
      if (!edit || edit.action !== 'update') return;
      const newLayer = (
        edit as { action: 'update'; data: { layer: WorkoutLayer } }
      ).data.layer;
      const updated = [newLayer, ...layersRef.current.slice(1)];
      layersRef.current = updated;
      setLocalLayers(updated);
      onLayersChange?.(updated);
    },
    [onLayersChange],
  );

  const mutationFns = useMemo(
    () =>
      makeWorkoutLayerMutationFns(
        'BlockDraftWorkoutLayer',
        workoutLayersForMutation,
        mutateFn,
        setsMetric,
      ),
    [workoutLayersForMutation, mutateFn, setsMetric],
  );

  const workout = useMemo(
    () =>
      workoutLayersForMutation.length > 0 && workoutLayersForMutation[0]
        ? mergeWorkoutLayers(
            workoutLayersForMutation[0],
            ...workoutLayersForMutation.slice(1),
          )
        : null,
    [workoutLayersForMutation],
  );

  const sections = useMemo(() => workout?.sections ?? [], [workout]);

  const mockWorkout = useMemo(
    () => ({
      workoutId: 'template',
      sections,
      cycle: { id: 1, name: 'Template', startDate: '', endDate: '' },
      session: { id: 1, name: 'Template' },
      exists: true,
      actual: undefined,
      status: { type: 'draft' as const },
      assignedDate: null,
      modifiedPublished: undefined,
      isAthleteOnlyLayer: false,
      name: null,
    }),
    [sections],
  );

  const { maxSetsColumns, maxNonSetsColumns, maxMetricsPerExercise } = useMemo(
    () => calculateSheetDimensions([mockWorkout], null, 'planned'),
    [mockWorkout],
  );

  const shouldShowSetsColumn = showSetsColumn ?? !readOnly;

  const slots = useMemo(
    () =>
      getSlotsForWorkoutTemplate({
        sections,
        workout,
        getMetric,
        noteColor: greyscaleLight[400],
        maxSetsColumns,
        maxNonSetsColumns,
        t,
        isReadOnly: readOnly,
        showSetsColumn: shouldShowSetsColumn,
        collapsedSections,
        mutationFns: readOnly ? undefined : mutationFns,
        setModalOrDrawerOpen: readOnly ? undefined : setIsModalOrDrawerOpen,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sections,
      workout,
      getMetric,
      greyscaleLight,
      maxSetsColumns,
      maxNonSetsColumns,
      t,
      readOnly,
      shouldShowSetsColumn,
      mutationFns,
      collapsedSections,
    ],
  );

  const gridTemplateColumns = useMemo(
    () =>
      getGridTemplateColumns(
        [mockWorkout],
        maxNonSetsColumns,
        maxMetricsPerExercise,
        'planned',
        !shouldShowSetsColumn, // hide the Sets column when not shown — same contract as block sheets
      ),
    [
      mockWorkout,
      maxNonSetsColumns,
      maxMetricsPerExercise,
      shouldShowSetsColumn,
    ],
  );

  const bulkRunCommands = useCallback(
    (commands: WorkoutLayerCommand[]) => {
      commands.forEach(mutateFn);
    },
    [mutateFn],
  );

  const [workoutClipboard, setWorkoutClipboard] = useAtom(workoutClipboardAtom);

  const onCopy = useCallback(
    (data: readonly BlockSelectionData[]) => {
      const groupedData = groupSelectionData(data);
      if (groupedData.length === 0) return;
      const days = groupedData.map((wgd) => ({
        assignedDate: null,
        data: wgd,
      }));
      setWorkoutClipboard(buildWorkoutClipboard(days));
    },
    [setWorkoutClipboard],
  );

  const onPaste = useCallback(
    (_: undefined, targetData: readonly BlockSelectionData[]) => {
      if (!workoutClipboard) return;
      const groupedTargetData = groupSelectionData(targetData);
      const clipboardData = workoutClipboard.days[0]?.data;
      if (!clipboardData) return;
      const result = pasteCommands(
        'BlockDraftWorkoutLayer',
        workoutLayersForMutation,
        clipboardData,
        groupedTargetData,
        setsMetric,
        undefined,
      );
      if (result.ok) {
        bulkRunCommands(result.value);
      } else {
        toast({ title: result.error, severity: 'error' });
      }
    },
    [workoutClipboard, workoutLayersForMutation, setsMetric, bulkRunCommands],
  );

  const handleContextMenu = useCallback(
    (data: readonly BlockSelectionData[]) => {
      const groupedData = groupSelectionData(data);
      return getContextMenuContent(
        groupedData,
        getMetric,
        metrics,
        workoutLayersForMutation,
        mutationFns,
        bulkRunCommands,
        setInstructionEditData,
        () => {},
        () => {},
        t,
        'BlockDraftWorkoutLayer',
        { navbarTopColor: '', navbarBottomColor: '' },
        { onSelectWorkoutTemplate: () => {}, onSelectScheme: () => {} },
        defaultBenchmarkGridId,
        false,
        false,
        [],
        undefined,
        undefined,
        'all',
      );
    },
    [
      getMetric,
      metrics,
      workoutLayersForMutation,
      mutationFns,
      bulkRunCommands,
      t,
      defaultBenchmarkGridId,
    ],
  );

  if (metricsLoading) return null;

  return (
    <>
      <Sheet
        {...slots}
        gridTemplateColumns={gridTemplateColumns}
        className={cn('overflow-auto', sheetClassName)}
        onUndo={() => {}}
        onRedo={() => {}}
        onCopy={onCopy}
        onPaste={onPaste}
        onBulkClear={() => {}}
        getContextMenuContent={readOnly ? () => null : handleContextMenu}
        onDragEnd={
          readOnly || !workout
            ? () => {}
            : (event: DragEndEvent) =>
                handleDragEnd(event, [workout], mutationFns)
        }
        disableKeyboardAndMouse={
          readOnly || !!instructionEditData || isModalOrDrawerOpen
        }
      />
      {!readOnly && instructionEditData && (
        <InstructionDrawer
          instruction={instructionEditData.instruction}
          setInstructionEditData={setInstructionEditData}
          editInstruction={(newInstruction) => {
            if (instructionEditData.workoutExerciseId) {
              mutationFns.onEditExercise(
                instructionEditData.workoutId,
                instructionEditData.workoutExerciseId,
                { instruction: newInstruction },
              );
            } else {
              mutationFns.onEditSection(
                instructionEditData.workoutId,
                instructionEditData.workoutSectionId,
                { instruction: newInstruction },
              );
            }
            setInstructionEditData(null);
          }}
          maxLength={instructionEditData.workoutExerciseId ? 300 : 1000}
        />
      )}
    </>
  );
};

export function getSlotsForWorkoutTemplate({
  sections,
  workout,
  getMetric,
  noteColor,
  maxSetsColumns,
  maxNonSetsColumns,
  t,
  isReadOnly,
  showSetsColumn,
  collapsedSections,
  mutationFns,
  setModalOrDrawerOpen,
}: {
  sections: WorkoutSection[];
  workout?: Workout | null;
  getMetric: (id: string) => Metric;
  noteColor: string;
  maxSetsColumns: number;
  maxNonSetsColumns: number;
  t: I18nTranslator;
  isReadOnly: boolean;
  /** Caller-decided; WorkoutTemplateSheet defaults it to `!readOnly`. */
  showSetsColumn: boolean;
  collapsedSections?: Set<string>;
  mutationFns?: WorkoutLayerMutationFns;
  setModalOrDrawerOpen?: (open: boolean) => void;
}) {
  const slots: BlockSlot[] = [];

  let currentY = 0;
  const currentX = 0;

  const isEditable = !isReadOnly && !!mutationFns;
  const onEditSetMetric = mutationFns?.onEditSetMetric;

  const supersetIndexMap = getSupersetIndexMap(sections);

  // Align with getGridTemplateColumns / getSlotsForBlock: show Sets when editable,
  // not only when calculateSheetDimensions found a sets metric on an exercise.
  const uniformWorkoutWidth = (showSetsColumn ? 1 : 0) + maxNonSetsColumns;

  const gridStyle = getWorkoutTemplateGridStyle(uniformWorkoutWidth);

  const allSectionNames = sections.map((s) => s.name);

  for (const section of sections) {
    const { slots: sectionSlots, rowCount: sectionRowCount } = getSectionSlot(
      currentX,
      currentY,
      uniformWorkoutWidth,
      section,
      noteColor,
      gridStyle,
      isReadOnly,
      t,
      allSectionNames,
      mutationFns?.onEditSection,
    );
    slots.push(...sectionSlots);
    currentY += sectionRowCount;

    if (collapsedSections?.has(section.workoutSectionId)) {
      continue;
    }

    for (const { exercise, superset, alternateGroup } of exercisesForSection(
      section,
    )) {
      const { slots: exerciseSlots, rowCount: exerciseHeight } =
        getExerciseSlots(
          section,
          superset,
          alternateGroup,
          exercise,
          currentX,
          currentY,
          uniformWorkoutWidth,
          getMetric,
          maxSetsColumns,
          maxNonSetsColumns,
          t,
          isReadOnly,
          showSetsColumn,
          supersetIndexMap,
          onEditSetMetric,
          mutationFns,
          gridStyle,
          setModalOrDrawerOpen,
        );
      slots.push(...exerciseSlots);
      currentY += exerciseHeight;
    }

    if (isEditable) {
      const { slots: addExerciseSlots, rowCount: addExerciseRowCount } =
        getAddExerciseSlot(
          section,
          currentX,
          currentY,
          uniformWorkoutWidth,
          false,
          mutationFns,
          setModalOrDrawerOpen,
          undefined,
          {
            style: gridStyle.style,
            overrideGridStyles: gridStyle.overrideGridStyles,
          },
        );
      slots.push(...addExerciseSlots);
      currentY += addExerciseRowCount;
    }
  }

  if (isEditable && workout) {
    const addSectionSlot: BlockSlot = new CustomSlot(
      { x: currentX, y: currentY },
      uniformWorkoutWidth,
      1,
      false,
      (editing, onBlur) => {
        if (editing) {
          return (
            <CellInputWithAtom inputAtom={sectionInputAtom} onBlur={onBlur} />
          );
        }
        return <AddTemplateSectionLabel />;
      },
      {
        data: [
          {
            type: 'AddSection' as const,
            workoutId: workout.workoutId,
            workout,
          },
        ],
        editBehavior: {
          type: 'input' as const,
          onStart: (input: string | null | undefined) => {
            jotaiStore.set(sectionInputAtom, input ?? '');
          },
          onCommit: () => {
            const input = jotaiStore.get(sectionInputAtom);
            if (sections.map((s) => s.name).includes(input)) {
              toast({ title: t('sectionNameMustBeUnique'), severity: 'error' });
              return;
            }
            if (input.length > 0) {
              mutationFns!.onAddSection(workout.workoutId, {
                name: input,
                sortOrder: nextSortOrder(sections),
                instruction: null,
                color: null,
              });
            }
          },
        },
        style: gridStyle.style,
        overrideGridStyles: gridStyle.overrideGridStyles,
      },
      ADD_SECTION_SLOT,
    );
    slots.push(addSectionSlot);
    currentY += 1;
  }

  return {
    slots,
    rowCount: currentY,
    columnCount: currentX + uniformWorkoutWidth,
  };
}

const getSectionSlot = (
  x: number,
  y: number,
  workoutWidth: number,
  section: WorkoutSection,
  noteColor: string,
  gridStyle: { style: React.CSSProperties; overrideGridStyles: boolean },
  isReadOnly: boolean,
  t: I18nTranslator,
  allSectionNames?: string[],
  onEditSection?: WorkoutLayerMutationFns['onEditSection'],
) => {
  const canEdit = !isReadOnly && !!onEditSection;

  const slot: BlockSlot = new CustomSlot(
    { x, y },
    workoutWidth,
    1,
    isReadOnly,
    (editing, onBlur) => {
      if (editing && canEdit) {
        return (
          <CellInputWithAtom inputAtom={sectionInputAtom} onBlur={onBlur} />
        );
      }
      if (canEdit) {
        return (
          <TemplateSectionLabelDraggable
            section={section}
            iconColor={noteColor}
          />
        );
      }
      return (
        <div className={'relative h-full w-full'}>
          <div className={cn(blockSectionAndExerciseLabelStyle, 'px-0')}>
            <TemplateSectionLabel section={section} iconColor={noteColor} />
          </div>
        </div>
      );
    },
    {
      data: [
        {
          type: 'Section',
          workoutId: section.workoutId,
          section,
          disabled: false,
        },
      ],
      ...(canEdit && {
        editBehavior: {
          type: 'input' as const,
          onStart: (initInput: string | null | undefined) => {
            jotaiStore.set(sectionInputAtom, initInput ?? section.name);
          },
          onCommit: () => {
            const input = jotaiStore.get(sectionInputAtom);
            if (input !== section.name && allSectionNames?.includes(input)) {
              toast({ title: t('sectionNameMustBeUnique'), severity: 'error' });
              return;
            }
            if (input.length > 0) {
              onEditSection(section.workoutId, section.workoutSectionId, {
                name: input,
              });
            }
          },
        },
      }),
      style: gridStyle.style,
      overrideGridStyles: gridStyle.overrideGridStyles,
    },
  );

  return { slots: [slot], rowCount: 1 };
};

const TemplateSectionLabelDraggable = ({
  section,
  iconColor,
}: {
  section: WorkoutSection;
  iconColor: string;
}) => {
  const { t } = useTranslation();
  const data: WorkoutSectionDraggable = {
    workoutSectionId: section.workoutSectionId,
    type: 'WorkoutSection',
    workoutId: section.workoutId,
  };
  const dndState = useDndRow(
    section.workoutSectionId,
    data,
    sectionDndStyleInfo,
  );
  const label = (
    <TemplateSectionLabel section={section} iconColor={iconColor} />
  );

  return (
    <DraggableLabelWrapper
      dndState={dndState}
      ariaLabel={t('dragSection')}
      dragOverlayChildren={label}
    >
      {label}
    </DraggableLabelWrapper>
  );
};

const TemplateSectionLabel = ({
  section,
  iconColor,
}: {
  section: WorkoutSection;
  iconColor: string;
}) => {
  const { t } = useTranslation();
  const {
    navbar: { navbarBottomColor },
  } = useViewer();
  const [collapsedSections, setCollapsedSections] = useAtom(
    collapsedSectionsAtom,
  );
  const sectionKey = section.workoutSectionId;
  const isCollapsed = collapsedSections.has(sectionKey);

  const toggleCollapse = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCollapsedSections((prev: Set<string>) => {
        const next = new Set(prev);
        if (next.has(sectionKey)) {
          next.delete(sectionKey);
        } else {
          next.add(sectionKey);
        }
        return next;
      });
    },
    [setCollapsedSections, sectionKey],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleCollapse(e as unknown as React.MouseEvent);
    }
  };

  return (
    <div className="overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-base flex items-center justify-between w-full ml-1.5">
      <div className="flex items-center gap-1">
        <CollapseSlotIcon
          onClick={toggleCollapse}
          onKeyDown={handleKeyDown}
          isCollapsed={isCollapsed}
          name={t(isCollapsed ? 'expandSection' : 'collapseSection', {
            sectionName: section.name,
          })}
        />
        <div className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
          {section.name}
        </div>
        {section.instruction && <MdOutlineNote size={18} color={iconColor} />}
      </div>
      {section.color && section.color !== navbarBottomColor && (
        <div
          className="w-3 h-3 rounded-sm"
          style={{ backgroundColor: section.color }}
        />
      )}
    </div>
  );
};

const AddTemplateSectionLabel = () => {
  const { t } = useTranslation();
  return (
    <span className="flex text-base px-2.5 py-2 items-center w-full text-gray-500">
      {t('addSection')}
    </span>
  );
};

const inputAtom = atom<string>('');

function getExerciseSlots(
  section: WorkoutSection,
  superset: WorkoutSuperset | null,
  alternateGroup: WorkoutAlternateGroup | null,
  exercise: WorkoutExercise,
  x: number,
  y: number,
  workoutWidth: number,
  getMetric: (id: string) => Metric,
  maxSetsColumns: number,
  maxNonSetsColumns: number,
  t: I18nTranslator,
  isReadOnly: boolean,
  showSetsColumn: boolean,
  supersetIndexMap?: Map<string, number>,
  onEditSetMetric?: (
    workoutId: string,
    workoutSetId: string,
    data: SetMetricMutationData,
  ) => void,
  mutationFns?: WorkoutLayerMutationFns,
  gridStyle?: { style: React.CSSProperties; overrideGridStyles: boolean },
  setModalOrDrawerOpen?: (open: boolean) => void,
): SlotsResults {
  const slots: BlockSlot[] = [];
  let rowCount = 0;

  const altExercise = alternateGroup?.exercises.find(
    (a) => a.workoutExerciseId === exercise.workoutExerciseId,
  );

  const onSwap = (swapExercise: BlockBuilderExercise) => {
    if (!mutationFns) return;

    const args = buildSwapExerciseArgs(
      section,
      exercise,
      superset,
      alternateGroup,
      swapExercise,
    );

    mutationFns.onSwapExercise(
      args.workoutId,
      args.workoutSectionId,
      args.previousWorkoutExerciseId,
      args.data,
      args.metrics,
      args.sets,
      args.superset,
      args.alternateGroup,
    );
  };

  slots.push(
    new CustomSlot(
      { x, y: y + rowCount },
      workoutWidth,
      1,
      isReadOnly,
      (editing, onBlur) =>
        isReadOnly ? (
          <ExerciseLabel
            exercise={exercise}
            isAlternate={!!altExercise && altExercise?.sortOrder !== 0}
            superset={superset}
            supersetIndexMap={supersetIndexMap}
          />
        ) : (
          <TemplateExerciseCell
            exercise={exercise}
            isAlternate={!!altExercise && altExercise?.sortOrder !== 0}
            superset={superset}
            alternateGroup={alternateGroup}
            supersetIndexMap={supersetIndexMap}
            workoutId={section.workoutId}
            editing={editing}
            onBlur={onBlur}
            onSwap={onSwap}
            setModalOrDrawerOpen={setModalOrDrawerOpen}
          />
        ),
      {
        data: [
          {
            type: 'Exercise',
            workoutId: section.workoutId,
            exercise: exercise,
            section,
            superset,
            disabled: isReadOnly,
            alternateGroup,
          },
        ],
        // Ignored when isReadOnly — CustomSlot.getEditBehavior() short-circuits
        // to 'skip' whenever the slot itself is disabled.
        editBehavior: {
          type: 'input',
          onStart: (input) =>
            jotaiStore.set(sectionInputAtom, input ?? exercise.exerciseName),
          onCommit: () => {},
        },
      },
      EXERCISE_NAME_SLOT,
    ),
  );
  rowCount += 1;

  if (exercise.metrics.length !== 0) {
    // When the Sets column is hidden, drop the Sets metric so the metric count
    // matches workoutWidth (which excludes it) — same contract as
    // getSlotsForBlock in block/slots/exercises.tsx. Otherwise the trailing
    // metric makes hiddenColumnsCount negative and the grid breaks.
    // `showSetsColumn` (not `!isReadOnly`) drives this: read-only hosts that
    // opt in — the drawer preview and workout-import preview — still render
    // the Sets column with its values, since templates store the compact
    // sets-metric draft form where the Sets value IS the prescription.
    const showsSetsColumn = showSetsColumn;
    const visibleMetrics = showsSetsColumn
      ? exercise.metrics
      : exercise.metrics.filter((m) => m.metricType !== 'sets');
    const hiddenColumnsCount = workoutWidth - visibleMetrics.length;
    slots.push(
      new MetricsSlot(
        isReadOnly,
        () => isReadOnly,
        { x, y: y + rowCount },
        sortByOrder(visibleMetrics),
        exercise.sets,
        (edit, set) => {
          if (!isReadOnly && onEditSetMetric) {
            const curValue = set.metrics.find(
              (m) => m.metricId === edit.metricId,
            )?.input;
            if (curValue !== edit.input) {
              onEditSetMetric(section.workoutId, set.workoutSetId, {
                metricId: edit.metricId,
                input: edit.input,
              });
            }
          }
        },
        inputAtom,
        (props) => (
          <SetMetricCellContent
            {...props}
            context={{ ...props.context, getMetric }}
          />
        ),
        {
          workoutErrors: new Map(),
          applyBenchmarkData: undefined,
          libraryExerciseId: exercise.libraryExerciseId,
        },
        0,
        hiddenColumnsCount,
        getMetric,
        {
          libraryExerciseId: exercise.libraryExerciseId,
          rawDataFn: (
            rows,
            rowMetrics,
            headerMetrics,
          ): BlockSelectionData[] => [
            ...rows.map((set) => ({
              workoutId: section.workoutId,
              type: 'Set' as const,
              set,
              metrics: rowMetrics,
              exercise,
              disabled: isReadOnly,
            })),
            ...headerMetrics.map((metric) => ({
              workoutId: section.workoutId,
              type: 'ExerciseMetric' as const,
              metric,
              exercise,
              disabled: isReadOnly,
            })),
          ],
        },
        'opacity-100',
        {
          workoutExerciseId: exercise.workoutExerciseId,
          workoutSectionId: section.workoutSectionId,
          workoutId: section.workoutId,
        },
        {
          setsColumnCount: maxSetsColumns,
          nonSetsColumnCount: maxNonSetsColumns,
          workoutStartColumn: 0,
          isReadOnly,
          shouldShowSetsColumn: showSetsColumn,
        },
      ),
    );
    rowCount += exercise.sets.length + 1;

    if (!isReadOnly && mutationFns) {
      const onAddSet = () => {
        mutationFns.onAddSet(
          section.workoutId,
          exercise.workoutExerciseId,
          { sortOrder: nextSortOrder(exercise.sets) },
          getDefaultSetMetrics(exercise.metrics),
        );
      };
      slots.push(
        new CustomSlot(
          { x, y: y + rowCount },
          workoutWidth,
          1,
          false,
          () => <AddSetButton onClick={onAddSet} />,
          {
            data: [{ workoutId: section.workoutId, type: 'AddSet', exercise }],
            editBehavior: { type: 'trigger', onEnter: onAddSet },
            className: 'p-0',
            style: gridStyle?.style,
            overrideGridStyles: gridStyle?.overrideGridStyles,
          },
          ADD_SET_SLOT,
        ),
      );
      rowCount += 1;
    }
  }

  if (
    isLastExerciseInGroup(alternateGroup, exercise.workoutExerciseId) &&
    !isReadOnly &&
    mutationFns
  ) {
    const { slots: altSlots, rowCount: altRowCount } = getAddExerciseSlot(
      section,
      x,
      y + rowCount,
      workoutWidth,
      false,
      mutationFns,
      undefined,
      alternateGroup?.workoutAlternateGroupId,
      gridStyle,
    );
    slots.push(...altSlots);
    rowCount += altRowCount;
  }

  return { slots, rowCount };
}

// A single component that stays mounted across the editing/non-editing
// toggle (unlike a component that only renders while `editing` is true) so
// modal state set right as editing ends (e.g. clicking "see more" or
// "create exercise", which blurs the search input and ends editing in the
// same batched update) survives into the next render instead of being
// unmounted before the modal ever shows.
const TemplateExerciseCell = ({
  exercise,
  isAlternate,
  superset,
  alternateGroup,
  supersetIndexMap,
  workoutId,
  editing,
  onBlur,
  onSwap,
  setModalOrDrawerOpen,
}: {
  exercise: WorkoutExercise;
  isAlternate: boolean;
  superset: WorkoutSuperset | null;
  alternateGroup: WorkoutAlternateGroup | null;
  supersetIndexMap?: Map<string, number>;
  workoutId: string;
  editing: boolean;
  onBlur: () => void;
  onSwap: (exercise: BlockBuilderExercise) => void;
  setModalOrDrawerOpen?: (open: boolean) => void;
}) => {
  const { team } = useViewer();
  const onCreateExercise = useCreateExercise(onSwap);

  const [advancedSearch, setAdvancedSearch] = useState<{
    searchTerm: string;
    open: boolean;
  }>({ searchTerm: '', open: false });
  const [createExerciseSheetOpen, setCreateExerciseSheetOpen] = useState(false);
  const [exerciseSearchTerm, setExerciseSearchTerm] = useState<string>('');

  if (editing) {
    return (
      <ExerciseSearch
        onBlur={onBlur}
        onSelect={onSwap}
        onOpenAdvancedSearch={(term) => {
          setModalOrDrawerOpen?.(true);
          setAdvancedSearch({ searchTerm: term, open: true });
        }}
        setCreateExerciseSheetOpen={(searchTerm) => {
          setModalOrDrawerOpen?.(true);
          setCreateExerciseSheetOpen(true);
          setExerciseSearchTerm(searchTerm);
        }}
      />
    );
  }

  return (
    <>
      <TemplateExerciseLabelDraggable
        exercise={exercise}
        isAlternate={isAlternate}
        superset={superset}
        alternateGroup={alternateGroup}
        supersetIndexMap={supersetIndexMap}
        workoutId={workoutId}
      />
      {advancedSearch.open && (
        <AdvancedExerciseSearchModal
          id={exercise.workoutExerciseId}
          initialSearchTerm={advancedSearch.searchTerm}
          open={advancedSearch.open}
          onClose={() => {
            setModalOrDrawerOpen?.(false);
            setAdvancedSearch({ searchTerm: '', open: false });
          }}
          setOpenCreateExerciseSheet={(searchTerm) => {
            setCreateExerciseSheetOpen(true);
            setAdvancedSearch({ searchTerm: '', open: false });
            setExerciseSearchTerm(searchTerm);
          }}
          onAddExercise={(exercise) => {
            onSwap(exercise);
            setAdvancedSearch({ searchTerm: '', open: false });
            setModalOrDrawerOpen?.(false);
          }}
          teamId={team.selectedTeam}
        />
      )}
      <CreateEditExerciseSheet
        open={createExerciseSheetOpen}
        onOpenChange={() => {
          setModalOrDrawerOpen?.(false);
          setCreateExerciseSheetOpen(false);
          setExerciseSearchTerm('');
        }}
        onSubmit={onCreateExercise}
        defaultValues={{ name: exerciseSearchTerm }}
        teamId={team.selectedTeam}
        showTeamName={true}
      />
    </>
  );
};

const TemplateExerciseLabelDraggable = ({
  exercise,
  isAlternate,
  superset,
  alternateGroup,
  supersetIndexMap,
  workoutId,
}: {
  exercise: WorkoutExercise;
  isAlternate: boolean;
  superset: WorkoutSuperset | null;
  alternateGroup: WorkoutAlternateGroup | null;
  supersetIndexMap?: Map<string, number>;
  workoutId: string;
}) => {
  const { t } = useTranslation();
  const data: WorkoutExerciseDraggable = {
    workoutExerciseId: exercise.workoutExerciseId,
    workoutSupersetId: superset?.workoutSupersetId ?? null,
    type: 'WorkoutExercise',
    workoutSectionId: exercise.workoutSectionId,
    workoutAlternateGroupId: alternateGroup?.workoutAlternateGroupId ?? null,
    workoutId,
  };
  const dndState = useDndRow(
    `WorkoutExercise:${exercise.workoutExerciseId}`,
    data,
    exerciseDndStyleInfo,
  );

  return (
    <DraggableLabelWrapper
      dndState={dndState}
      ariaLabel={t('dragExercise')}
      labelPadding="px-1"
      droppablePadding="px-2.5 py-2"
      dragOverlayChildren={
        <ExerciseText exercise={exercise} isAlternate={isAlternate} />
      }
    >
      <ExerciseText exercise={exercise} isAlternate={isAlternate} />
      <div className="w-2 flex-1" />
      {superset && (
        <SupersetIcon
          workoutSupersetId={superset.workoutSupersetId}
          supersetIndex={supersetIndexMap?.get(superset.workoutSupersetId) ?? 0}
        />
      )}
    </DraggableLabelWrapper>
  );
};

const ExerciseLabel = ({
  exercise,
  isAlternate,
  superset,
  supersetIndexMap,
}: {
  exercise: WorkoutExercise;
  isAlternate: boolean;
  superset: WorkoutSuperset | null;
  supersetIndexMap?: Map<string, number>;
}) => {
  return (
    <div className={'relative h-full w-full'}>
      <div className={cn(blockSectionAndExerciseLabelStyle, 'px-1')}>
        <ExerciseText exercise={exercise} isAlternate={isAlternate} />
        <div className="w-2 flex-1" />
        {superset && (
          <SupersetIcon
            workoutSupersetId={superset.workoutSupersetId}
            supersetIndex={
              supersetIndexMap?.get(superset.workoutSupersetId) ?? 0
            }
          />
        )}
      </div>
    </div>
  );
};
